"use client";

import React, { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Monitor, Wind, Gamepad2, CircleCheck, Clock } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const serviceCategories = [{
  id: "computers",
  label: "Fix My Computer",
  icon: Monitor,
  services: [{
    name: "Basic PC Tune-Up",
    price: "$40",
    description: "Make your computer run faster by cleaning up old apps and fixing slow startups.",
    tag: "No Drop-Off Needed",
    availability: "Same Day",
    popular: false,
    img: "https://sway-systems.com/wp-content/uploads/2026/05/image_2026-05-05_212152101.png?v=wwmbzej3kq9"
  }, {
    name: "Full System Cleanup",
    price: "$60",
    description: "A deep clean that removes junk files and hidden programs that slow you down.",
    tag: "Most Popular",
    availability: "Same Day",
    popular: true,
    img: "https://sway-systems.com/wp-content/uploads/2026/04/repair_custom_loop.png?v=t8pctz1lez"
  }, {
    name: "File Backup Help",
    price: "$40–$60",
    description: "We safely copy your photos, documents, and videos to a USB drive or external storage so you don't lose them.",
    tag: "No Parts Needed",
    availability: "Same Day",
    popular: false,
    img: "https://sway-systems.com/wp-content/uploads/2026/05/image_2026-05-05_212106638.png?v=7s1ddzt926d"
  }, {
    name: "Windows Updates & Fixes",
    price: "$30–$50",
    description: "Fixing errors, installing the latest updates, and making sure Windows runs smoothly.",
    tag: "Remote Available",
    availability: "Same Day",
    popular: false,
    img: "https://sway-systems.com/wp-content/uploads/2026/05/image_2026-05-05_212235805.png?v=j3lw972hoxe"
  }, {
    name: "Complete PC Help Bundle",
    price: "$80–$100",
    description: "The total package: we speed up your PC, back up your files, and show you how to keep it running great.",
    tag: "Best Value",
    availability: "1–2 Days",
    popular: true,
    img: "https://sway-systems.com/wp-content/uploads/2026/04/custom_pc_assembly.png?v=eokdzeqfuwc"
  }, {
    name: "New PC Setup & Transfer",
    price: "$90–$120",
    description: "We'll help you pick a new computer, set it up, and move all your old files over for you.",
    tag: "Drop-Off Required",
    availability: "1–2 Days",
    popular: false,
    img: "https://sway-systems.com/wp-content/uploads/2026/05/Transfer-Windows-Activate-It-from-an-Old-to-a-New-PC.png?v=meupph26mc"
  }, {
    name: "Hardware Repair",
    price: "$25–$35/hr + parts",
    description: "Fixing broken parts like screens, keyboards, or internal components. Price is for labor.",
    tag: "Hourly Rate",
    availability: "2–3 Days",
    popular: false,
    img: "https://sway-systems.com/wp-content/uploads/2026/04/repair_motherboard_solder.png?v=15lw9eqtjoa"
  }, {
    name: "Screen Replacement",
    price: "$90–$120",
    description: "Replacing cracked or broken laptop screens with a brand new display.",
    tag: "Flat Rate",
    availability: "1–2 Days",
    popular: false,
    img: "https://sway-systems.com/wp-content/uploads/2026/05/image_2026-05-05_211840835.png?v=c21og5j2c4t"
  }]
}, {
  id: "remote",
  label: "Help From Home",
  icon: Monitor,
  services: [{
    name: "Remote Support",
    price: "$25–$35/hr",
    description: "Fixing your computer problems over the internet while you sit back and watch.",
    tag: "Hourly Rate",
    availability: "Same Day",
    popular: true,
    img: "https://sway-systems.com/wp-content/uploads/2026/05/image_2026-05-05_212527560.png?v=36x4csz4p5v"
  }, {
    name: "Windows OS Installer",
    price: "$20–$30",
    description: "Setting up a fresh copy of Windows on your computer using a USB drive.",
    tag: "Remote Available",
    availability: "Same Day",
    popular: false,
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652639/img/windows_os_installer.png"
  }, {
    name: "Linux OS Installer",
    price: "$20–$30",
    description: "Installing a fast, free alternative system like Linux on your computer.",
    tag: "Remote Available",
    availability: "Same Day",
    popular: false,
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652639/img/linux_os_installer.png"
  }, {
    name: "AnyDesk Remote Desktop Setup",
    price: "$0 (Free)",
    description: "A quick setup to let our technician securely connect to your computer and fix things from afar.",
    tag: "Remote Available",
    availability: "Same Day",
    popular: false,
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652639/img/anydesk_remote_setup.png"
  }]
}, {
  id: "cleaning",
  label: "Clean My PC",
  icon: Wind,
  services: [{
    name: "Basic PC Cleaning",
    price: "$40–$50",
    description: "Cleaning the dust out of your computer's fans and vents to keep it from overheating.",
    tag: "Drop-Off Required",
    availability: "Same Day",
    popular: false,
    img: "https://sway-systems.com/wp-content/uploads/2026/05/image_2026-05-05_213443497.png?v=1rcgk2z52bk"
  }, {
    name: "Deep PC Cleaning",
    price: "$70–$80",
    description: "A thorough internal cleaning to get rid of all the dust and grime inside your machine.",
    tag: "Drop-Off Required",
    availability: "Same Day",
    popular: false,
    img: "https://sway-systems.com/wp-content/uploads/2026/05/image_2026-05-05_213317927.png?v=do2nnbkzqmp"
  }, {
    name: "Deep Clean + Thermal Paste",
    price: "$80–$100",
    description: "A deep clean plus new cooling paste to keep your computer running cool and quiet.",
    tag: "Best Value",
    availability: "Same Day",
    popular: true,
    img: "https://sway-systems.com/wp-content/uploads/2026/05/image_2026-05-05_213640709.png?v=wuk45kbokyh"
  }, {
    name: "Fan Replacement",
    price: "$30+",
    description: "Replacing loud or broken fans with new, quiet ones to keep things cool.",
    tag: "Drop-Off Required",
    availability: "1–2 Days",
    popular: false,
    img: "https://sway-systems.com/wp-content/uploads/2026/05/image_2026-05-05_213758424.png?v=3izqz0lwcmu"
  }, {
    name: "Cable Management",
    price: "$50",
    description: "Tidying up messy wires inside your PC to make it look better and stay cooler.",
    tag: "Drop-Off Required",
    availability: "Same Day",
    popular: false,
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652639/img/pc_cable_management.png"
  }]
}, {
  id: "consoles",
  label: "Game Console Repair",
  icon: Gamepad2,
  services: [{
    name: "Basic Console Clean",
    price: "$20–$30",
    description: "Cleaning the outside of your game console and all the plug-in ports.",
    tag: "Drop-Off Required",
    availability: "Same Day",
    popular: false,
    img: "https://sway-systems.com/wp-content/uploads/2026/05/image_2026-05-05_214128324.png?v=9jx1htg4lts"
  }, {
    name: "Internal Dust Cleaning",
    price: "$40–$60",
    description: "Opening up your console to blow out the dust that causes loud fan noise.",
    tag: "Drop-Off Required",
    availability: "Same Day",
    popular: false,
    img: "https://sway-systems.com/wp-content/uploads/2026/05/image_2026-05-05_214406977.png?v=5ocy0ji2zui"
  }, {
    name: "Deep Clean + Thermal Paste",
    price: "$70–$90",
    description: "The best way to stop your console from overheating and sounding like a jet engine.",
    tag: "Most Popular",
    availability: "Same Day",
    popular: true,
    img: "https://sway-systems.com/wp-content/uploads/2026/05/image_2026-05-05_214809223.png?v=8dnevq08kwr"
  }, {
    name: "Controller Cleaning",
    price: "$15–$25",
    description: "Cleaning and disinfecting your controllers to fix sticky buttons and grime.",
    tag: "No Parts Needed",
    availability: "Same Day",
    popular: false,
    img: "https://sway-systems.com/wp-content/uploads/2026/05/image_2026-05-05_214524125.png?v=5wcieyg97s2"
  }, {
    name: "Console Hardware Repair",
    price: "$25–$35/hr + parts",
    description: "Fixing broken HDMI ports or disc drives so you can get back to gaming.",
    tag: "Drop-Off Required",
    availability: "2–3 Days",
    popular: false,
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652639/img/console_hardware_repair.png"
  }, {
    name: "Full Console Bundle",
    price: "$80–$100",
    description: "A complete refresh for your console and controllers to make them feel like new again.",
    tag: "Best Value",
    availability: "Same Day",
    popular: true,
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652639/img/full_console_refresh_bundle.png"
  }]
}];
interface ServiceCardProps {
  service: any;
  index: number;
  selectedService?: string | null;
  onServiceSelect?: (serviceName: string) => void;
  onBookService?: (serviceName: string) => void;
}
function ServiceCard({
  service,
  index,
  selectedService,
  onServiceSelect,
  onBookService
}: ServiceCardProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isSelected = selectedService === service.name;
  const tagColors: Record<string, string> = {
    "Most Popular": "bg-primary/20 text-primary border-primary/40",
    "Best Value": "bg-secondary/20 text-secondary border-secondary/40",
    "Remote Available": "bg-accent/20 text-accent-foreground border-accent/40",
    "Remote Only": "bg-accent/20 text-accent-foreground border-accent/40",
    "No Drop-Off Needed": "bg-emerald-500/20 text-emerald-500 border-emerald-500/40",
    "Drop-Off or Mobile": "bg-yellow-500/20 text-yellow-500 border-yellow-500/40",
    "Drop-Off Required": "bg-muted text-muted-foreground border-border",
    "No Parts Needed": "bg-muted text-muted-foreground border-border"
  };
  return <Card ref={ref} key={index} data-index={index} id={`service-${service.name.toLowerCase().replace(/\s+/g, '-')}`} className={cn("w-full bg-card/50 backdrop-blur-md border-2 border-primary/30 rounded-lg shadow-md hover:shadow-[0_0_25px_rgba(34,197,94,0.2)] hover:border-primary transition-all duration-300 flex flex-col h-full group overflow-hidden relative cursor-pointer p-4 sm:p-5 md:p-6", service.popular && "border-primary/60", isSelected && "border-primary shadow-[0_0_20px_rgba(34,197,94,0.4)] bg-primary/5", isVisible && "animate-[pulse-border-glow_0.6s_ease-in-out]")} onClick={() => onServiceSelect?.(service.name)}>
      {service.popular && <div className={cn("absolute top-0 left-0 z-20 text-[9px] font-default font-bold uppercase tracking-widest px-3 py-1", tagColors[service.tag] || "bg-primary text-primary-foreground")}>
          {service.tag}
        </div>}

      <div className="aspect-square sm:aspect-video lg:aspect-square xl:aspect-video overflow-hidden relative rounded-md">
        <img src={service.img} data-wvc-srcset={service.img.startsWith('http') ? undefined : `${service.img}-wvc-srcset`} data-wvc-sizes={service.img.startsWith('http') ? undefined : `${service.img}-wvc-sizes`} alt={service.name} loading="lazy" className="w-full h-full object-cover md:grayscale md:group-hover:grayscale-0 group-hover:grayscale-0 transition-all duration-500 scale-105 group-hover:scale-100" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-60" />
        <div className="absolute top-4 right-4 bg-primary/90 text-primary-foreground px-3 py-1 text-xs sm:text-sm font-bold font-default">
          {service.price}
        </div>
      </div>

      <CardHeader className="pb-2 px-0 relative z-10">
        <CardTitle className="font-default text-foreground font-bold text-base sm:text-lg md:text-xl leading-tight tracking-tight group-hover:text-primary transition-colors flex items-center gap-2 cursor-pointer">
          <CircleCheck className="w-4 h-4 text-primary shrink-0" />
          {service.name}
        </CardTitle>
        <div className="flex items-center gap-2 flex-wrap mt-1">
          {!service.popular && service.tag && <span className={cn("text-[9px] font-default uppercase tracking-widest border px-2 py-0.5 rounded-lg", tagColors[service.tag] || "bg-muted text-muted-foreground border-border")}>
              {service.tag}
            </span>}
          <span className="text-[9px] font-default uppercase tracking-widest text-muted-foreground/60 border border-border px-2 py-0.5 rounded-lg flex items-center gap-1">
             <Clock className="w-2.5 h-2.5" /> {service.availability}
          </span>
        </div>
      </CardHeader>

      <CardContent className="flex-grow pb-6 px-0 relative z-10">
        <CardDescription className="text-muted-foreground font-body-sans text-xs sm:text-sm leading-relaxed">
          {service.description}
        </CardDescription>
      </CardContent>

      <CardFooter className="pt-0 px-0 mt-auto relative z-10">
        <Button type="button" onClick={e => {
        e.stopPropagation();
        const params = new URLSearchParams(window.location.search);
        params.set('service', service.name);
        window.history.replaceState(null, '', '?' + params.toString());
        onBookService?.(service.name);
        document.getElementById('terminal-interface')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }} className="w-full bg-transparent border border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-lg min-h-[44px] font-default font-bold text-xs sm:text-sm uppercase tracking-widest transition-all duration-300">
          Book This Service
        </Button>
      </CardFooter>
    </Card>;
}
export default function DrainServicesGrid() {
  const [activeTab, setActiveTab] = useState("computers");
  const [searchParams] = useSearchParams();
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  useEffect(() => {
    const serviceParam = searchParams.get("service");
    if (serviceParam) {
      setSelectedService(serviceParam);
      const category = serviceCategories.find(cat => cat.services.some(s => s.name === serviceParam));
      if (category) {
        setActiveTab(category.id);
      }
    }
  }, [searchParams]);
  const updateServiceState = useCallback((serviceName: string) => {
    setSelectedService(serviceName);
    const url = new URL(window.location.href);
    url.searchParams.set("service", serviceName);
    window.history.replaceState({}, "", url.toString());
  }, []);
  const handleServiceSelect = useCallback((serviceName: string) => {
    updateServiceState(serviceName);
    const elementId = `service-${serviceName.toLowerCase().replace(/\s+/g, '-')}`;
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  }, [updateServiceState]);
  return <section data-section-id="242037" id="fullservicecatalog" className="bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8 border-t border-primary/20 scroll-mt-20">
      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes pulse-border-glow {
          0% { border-color: rgba(34, 197, 94, 0.3); box-shadow: none; }
          50% { border-color: #22c55e; box-shadow: 0 0 15px rgba(34, 197, 94, 0.5); }
          100% { border-color: rgba(34, 197, 94, 0.3); box-shadow: none; }
        }
      `
    }} />
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`text-center mb-12 transition-all duration-700 ease-out ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="font-default text-primary font-bold text-3xl md:text-5xl tracking-tighter uppercase italic">
            Complete Service Catalog
          </h2>
          <p className="mt-4 text-muted-foreground font-default max-w-2xl mx-auto text-sm md:text-base">Not sure what's wrong? Pick the category that sounds closest to your problem — I'll handle the rest.</p>
          <p className="mt-2 text-primary/60 font-mono text-[10px] uppercase tracking-[0.2em] max-w-2xl mx-auto">
            * All prices listed are for labor only. Replacement parts are billed separately based on current market rates.
          </p>
        </div>

        <div className="flex flex-col md:flex-row justify-center gap-2 mb-12">
          {serviceCategories.map((category, i) => {
          const Icon = category.icon;
          const isActive = activeTab === category.id;
          return <Button key={category.id} data-index={i} onClick={() => setActiveTab(category.id)} variant={isActive ? "default" : "outline"} className={cn("flex items-center justify-center gap-3 h-auto px-6 py-4 border-2 rounded-lg font-default text-sm uppercase tracking-widest transition-all duration-300", isActive ? "bg-primary text-primary-foreground border-primary shadow-[0_0_15px_rgba(34,197,94,0.5)]" : "bg-card/30 border-primary/20 text-muted-foreground hover:border-primary/50 hover:text-primary")}>
                <Icon className="w-5 h-5" />
                {category.label}
              </Button>;
        })}
        </div>

        <div ref={contentRef} className={`transition-all duration-1000 ease-out ${isContentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {serviceCategories.map((category, i) => <div key={category.id} data-index={i} className={cn("grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6", activeTab === category.id ? "grid" : "hidden")}>
              {category.services.map((service, index) => <ServiceCard key={index} service={service} index={index} selectedService={selectedService} onServiceSelect={handleServiceSelect} onBookService={updateServiceState} />)}
            </div>)}
        </div>
      </div>
    </section>;
}